import SvgIcon from '@mui/material/SvgIcon';
import React, { memo } from 'react';

import useScreenSize from '../../../util/hooks/useScreenSize';

/**
 * Logo component for VanSite
 * @param {object} props
 * @param {string} props.alt - Alternative text for the logo
 * @param {import("@mui/material").SxProps} props.sx - MUI style object
 * @param {string} props.title - Title for the logo
 * @returns {React.ReactNode} A logo component
 */

const LogoComponent = ({ ...rest }) => {
  const { isMobile } = useScreenSize();
  const desktopLogo = (
    <svg height="40" viewBox="0 0 422.1 154.3" xmlns="http://www.w3.org/2000/svg" {...rest}>
      <circle cx="77.2" cy="77.2" fill="#eac435" r="77.2" />
      <path
        d="M143.8 76.9h-3.3v.3a63.3 63.3 0 1 1-63.6-63.3v-3.4a66.7 66.7 0 1 0 67 66.7v-.3Z"
        fill="#0e3b43"
      />
      <g fill="#eac435">
        <path d="M136.7 79ZM140.5 79ZM136.7 79a1.9 1.9 0 1 1 3.8 0v-3.7h-3.8Z" />
      </g>
      <g fill="#eac435">
        <path d="M74.4 75.3ZM74.4 79ZM74.4 75.3a1.9 1.9 0 1 1 0 3.8h3.8v-3.8Z" />
      </g>
      <path
        d="M36 63.3 92.6 42c1-.4 2.3 0 2.3.9v21.4c0 .6-.7 1-1.6 1H36.7c-1.6 0-2.2-1.4-.8-2ZM125.4 92l-4 11.7a1 1 0 0 1-1 .7H35.2a1 1 0 0 1-1-.7L29 92a1.3 1.3 0 0 1 0-.7l5.2-22.8a1.2 1.2 0 0 1 1.2-1h81.3a1.2 1.2 0 0 1 1.2.9l7.5 23a1.3 1.3 0 0 1 0 .6ZM120.4 106c.8 0 1.3.6 1 1.1l-1 2a1.4 1.4 0 0 1-1.2.5H35.8a1.4 1.4 0 0 1-1.3-.6l-1-1.9c-.3-.5.3-1.1 1.1-1.1Z"
        fill="#0e3b43"
      />
      <circle cx="102.6" cy="106.9" fill="#0e3b43" r="11.1" stroke="#eac435" />
      <circle cx="51.7" cy="106.9" fill="#0e3b43" r="11.1" stroke="#eac435" />
      <circle cx="51.7" cy="106.9" fill="#eac435" r="5.3" />
      <circle cx="102.6" cy="106.9" fill="#eac435" r="5.3" />
      <path
        d="M115 65.3H99.7a1.2 1.2 0 0 1-1.3-1v-2.9a1.2 1.2 0 0 1 1.3-1.1h13.2a1.3 1.3 0 0 1 1 .5l2.1 2.8a1.1 1.1 0 0 1-1 1.7Z"
        fill="#0e3b43"
      />
      <path
        d="M98.3 72.4v11a1 1 0 0 0 1 1h18.2a1 1 0 0 0 1-1v-.9a1.1 1.1 0 0 0 0-.3L115.2 72a1 1 0 0 0-1-.7H99.4a1 1 0 0 0-1.1 1ZM36.8 84.4h12.7a1 1 0 0 0 1-1v-11a1 1 0 0 0-1-1H39.3a1 1 0 0 0-1 .9L35.7 83a1 1 0 0 0 1.1 1.3Z"
        fill="#eac435"
      />
      <rect fill="#eac435" height="13.1" rx="1.1" width="39.1" x="55.8" y="71.3" />
      <path
        d="m32.8 58.8 59-22.4a.7.7 0 0 1 .8.4l.8 1.9a.7.7 0 0 1-.5.9L34 62a.7.7 0 0 1-.9-.4l-.7-1.9a.7.7 0 0 1 .4-1Z"
        fill="#0e3b43"
      />
      <g fill="#0e3b43">
        <path d="m188.3 109.4-17.2-51.2h9.2l9.3 29a72.4 72.4 0 0 1 2 7.6l.6 2.7h.3l.5-2.5q.6-2.5 2.6-8.3l9.4-28.5h8.8l-17.6 51.2ZM235.7 99.6H222l-3.4 9.6H210L224.9 69h9l14.3 40.2h-9ZM224 93.3h10L229 76.2ZM267.2 90.7l-1.7-3.4-1.7-3.6-1.3-2.9-.5-1.1V81l.2 3.1.2 4v3.6l.3 17.5h-8v-40h9.2l10 18.1 1.5 3.1 1.5 3.3 1.5 3.7-.1-1.2a128.4 128.4 0 0 0-.5-6.6l-.1-3.4-.2-17h8v40h-8.7ZM331.6 99.9a15.4 15.4 0 0 1-3.8 5.2 18.6 18.6 0 0 1-6.3 3.7 25 25 0 0 1-8.6 1.4 33.1 33.1 0 0 1-8.7-1.2 30.5 30.5 0 0 1-8-3.5l3.2-6.6a43.1 43.1 0 0 0 6.7 3.1 21.4 21.4 0 0 0 7 1.2q4.8 0 7.5-2.2a7.3 7.3 0 0 0 2.6-6 6.3 6.3 0 0 0-1.8-4.6 12.9 12.9 0 0 0-5.8-3l-5.4-1.5a25.6 25.6 0 0 1-6-2.6 12.5 12.5 0 0 1-4-3.6 13.2 13.2 0 0 1-2.1-7.4 14.7 14.7 0 0 1 1.3-6.2 13.8 13.8 0 0 1 3.5-4.7 16.6 16.6 0 0 1 5.5-3.1 21.5 21.5 0 0 1 7-1.1 29.4 29.4 0 0 1 8.7 1.2 26.2 26.2 0 0 1 7.6 3.7l-3.8 6a36 36 0 0 0-6.4-3 18 18 0 0 0-5.8-1 9.3 9.3 0 0 0-6 2 6.2 6.2 0 0 0-2.3 5 4.9 4.9 0 0 0 1.5 3.8 14 14 0 0 0 5.3 2.5l6 1.8a20.6 20.6 0 0 1 9.2 5.2 12.8 12.8 0 0 1 3.5 9.3 15.2 15.2 0 0 1-1.3 6.2ZM343.6 69.2h8.6v40h-8.5ZM378.8 75.8v33.4h-8.5V75.8h-11v-6.6H391l-1.4 6.6ZM396.5 69.2h25l-1.2 6.6h-15.7V85h13.1v6.7h-13v10.6h17.4v7h-25.6Z" />
      </g>
    </svg>
  );

  const mobileLogo = (
    <svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" {...rest}>
      <circle cx="110" cy="110" fill="#eac435" r="110" />
      <path
        d="M205 109.6h-4.8v.4a90.3 90.3 0 1 1-90.6-90.3V15a95 95 0 1 0 95.4 95v-.4Z"
        fill="#0e3b43"
      />
      <path
        d="M51.2 85.5 132 55c1.5-.6 3.4.2 3.4 1.4v30.4c0 .8-1 1.5-2.3 1.5H52.3c-2.3 0-3.1-2-1-2.8ZM178.7 126.3 173 143a1.5 1.5 0 0 1-1.4 1H50.1a1.5 1.5 0 0 1-1.4-.9l-7.3-16.6a1.6 1.6 0 0 1-.1-1.1l7.5-32.5a1.7 1.7 0 0 1 1.6-1.3h116a1.7 1.7 0 0 1 1.6 1.1l10.8 32.8a1.9 1.9 0 0 1 0 .8ZM171.6 146.3c1.1 0 2 .9 1.5 1.6l-1.4 2.7a2 2 0 0 1-1.7.8H50.8a2 2 0 0 1-1.7-.8l-1.4-2.6c-.5-.8.4-1.6 1.5-1.6Z"
        fill="#0e3b43"
      />
      <g fill="#0e3b43" stroke="#eac435">
        <circle cx="146.2" cy="147.6" r="15.8" />
        <circle cx="73.7" cy="147.6" r="15.8" />
      </g>
      <circle cx="73.7" cy="147.6" fill="#eac435" r="7.5" />
      <circle cx="146.2" cy="147.6" fill="#eac435" r="7.5" />
      <path
        d="M163.9 88.4H142a1.7 1.7 0 0 1-1.8-1.6v-4a1.7 1.7 0 0 1 1.8-1.6h18.8a1.9 1.9 0 0 1 1.5.7l3 4a1.6 1.6 0 0 1-1.5 2.5Z"
        fill="#0e3b43"
      />
      <path
        d="M140.1 98.4V114a1.5 1.5 0 0 0 1.5 1.4h26a1.5 1.5 0 0 0 1.4-1.4v-1.2a1.4 1.4 0 0 0 0-.5L164 98a1.5 1.5 0 0 0-1.5-1h-20.9a1.5 1.5 0 0 0-1.5 1.5ZM52.5 115.6h18A1.4 1.4 0 0 0 72 114V98.4a1.4 1.4 0 0 0-1.4-1.5H56a1.6 1.6 0 0 0-1.5 1.2l-3.7 15.5a1.6 1.6 0 0 0 1.6 2Z"
        fill="#eac435"
      />
      <rect fill="#eac435" height="18.6" rx="1.6" width="55.8" x="79.5" y="96.9" />
      <path
        d="m46.8 79 84-32a1 1 0 0 1 1.3.7l1 2.7a1 1 0 0 1-.6 1.3l-84 31.9a1 1 0 0 1-1.3-.6l-1-2.7a1 1 0 0 1 .6-1.3Z"
        fill="#0e3b43"
      />
    </svg>
  );

  const svg = !isMobile ? desktopLogo : mobileLogo;

  return <SvgIcon {...rest}>{svg}</SvgIcon>;
};

LogoComponent.displayName = 'Logo';

const Logo = memo(LogoComponent);

export default Logo;
